import { accessTokenHash } from './accessTokenHash'
import type { AccessTokenMetaCookieValue } from './authCookies'

export type BfcacheAuthAction = 'reload-logout' | 'reload-session-change' | 'invalidate-token' | 'none'

/**
 * Decides how a page restored from the bfcache must reconcile its frozen,
 * in-memory session against the durable `kidsout__at_meta` cookie (which peer
 * tabs and SSR keep current). While frozen the page misses the BroadcastChannel
 * session-sync messages, so the cookie is the only signal of what changed.
 *
 * The marker shares the access token's expiry, so its absence is only
 * conclusive while the token has not yet expired: then it can only be gone
 * because it was explicitly cleared — a logout elsewhere. Once the token has
 * expired the marker drops naturally, so its absence is uninformative and the
 * normal on-demand refresh handles renewal.
 *
 * - `reload-logout` — we hold a still-valid session but the marker is gone:
 *   logged out elsewhere while frozen. Reload (clears cookies first).
 * - `reload-session-change` — a different subject is now signed in (account
 *   switch / re-login as someone else). The whole page must reload.
 * - `invalidate-token` — same subject, but a newer token was minted while frozen
 *   (a peer tab refreshed). Our in-memory token is stale; drop it so the next
 *   request refreshes from the current refresh cookie. No reload.
 * - `none` — nothing actionable: anonymous page, an expired token (defer to the
 *   refresh path), or the token still matches.
 */
export const decideBfcacheAuthAction = (
  snapshot: { user_id: string; access_token: string; expires_at: number } | null,
  meta: AccessTokenMetaCookieValue | null,
  nowMs: number
): BfcacheAuthAction => {
  if (!snapshot) return 'none'
  if (!meta) return nowMs < snapshot.expires_at ? 'reload-logout' : 'none'
  if (meta.user_id !== snapshot.user_id) return 'reload-session-change'
  if (meta.hash !== accessTokenHash(snapshot.access_token)) return 'invalidate-token'
  return 'none'
}
